import { resolveId } from '@medplum/core'
import type { Reference, ServiceRequest } from '@medplum/fhirtypes'
import {
  OrderCategory,
  orderCategoryLabel,
} from '@seen/core/models/orders/constants'
import { Order } from '@seen/core/models/orders/Order'
import { Participant } from '@seen/core/models/patients/participant'
import { Badge, cn, DotSeparator } from '@seen/ui'
import { type JSX, useContext } from 'react'
import { Link } from '#src/components/core/Link.tsx'
import { IconOrderStatus } from '#src/components/icons/IconOrderStatus.tsx'
import { PatientName } from '#src/components/patient/PatientName.tsx'
import { CurrentParticipantContext } from '#src/contexts/current-participant/CurrentParticipantContext.tsx'
import { useRead } from '#src/hooks/useRead.ts'
import { getOrderTitle } from '#src/utils/getOrderTitle.ts'

const ORDER_PREFIX_RE = /^(New|Doc'd Change|D\/C|Refill|Reorder|Discontinue)\s+/

export function OrderMention({
  order: orderReference,
  className,
  hideParticipant,
  displayOverride,
}: {
  order: Reference<ServiceRequest>
  className?: string
  hideParticipant?: boolean
  displayOverride?: string | null
}): JSX.Element {
  const orderId = resolveId(orderReference)

  const { data: order } = useRead(Order, orderReference)
  const participantId = resolveId(order?.participant)

  const title = getOrderTitle(order)
  const categoryLabel =
    !!order?.category && order.category !== OrderCategory.Misc
      ? orderCategoryLabel(order.category)
      : ''

  const currentParticipant = useContext(CurrentParticipantContext)
  const { data: participant } = useRead(Participant, participantId)

  if (displayOverride) {
    const prefixMatch = ORDER_PREFIX_RE.exec(displayOverride)
    const prefix = prefixMatch?.[1]
    const displayText = prefix
      ? displayOverride.slice(prefixMatch[0].length)
      : displayOverride

    return (
      <Link
        to={`/Patient/${participantId}/orders/${orderId}`}
        className={cn(
          'select-none rounded-sm border-[0.5px] border-gray-450 bg-gray-470 box-decoration-clone px-1 py-px hover:bg-gray-460 group-[.ProseMirror-selectednode]:bg-[lch(53_52.26_286.91/0.4)] [&&]:no-underline',
          className
        )}
        tabIndex={-1}
      >
        {prefix && (
          <Badge
            variant="outline"
            className="mr-1 inline-flex items-center align-[7%]!"
          >
            {prefix}
          </Badge>
        )}
        <span>{displayText}</span>
      </Link>
    )
  }

  return (
    <Link
      to={`/Patient/${participantId}/orders/${orderId}`}
      className={cn(
        'select-none rounded-sm border-[0.5px] border-gray-450 bg-gray-470 box-decoration-clone px-1 py-px hover:bg-gray-460 group-[.ProseMirror-selectednode]:bg-[lch(53_52.26_286.91/0.4)] [&&]:no-underline',
        className
      )}
      tabIndex={-1}
    >
      <span className="inline-flex items-center gap-1 align-middle whitespace-nowrap">
        <IconOrderStatus
          status={order?.status}
          isSubmitting={order?.isSubmitting}
          className="size-3.5"
        />
        <span className="text-foreground-secondary">
          {order?.seenIdentifier}
        </span>
        {!hideParticipant &&
          currentParticipant?.id !== participantId &&
          participant && (
            <span className="inline-flex items-center text-foreground-secondary">
              <DotSeparator />
              <PatientName participant={participant} />
              <DotSeparator />
            </span>
          )}
      </span>{' '}
      <span>
        {!!categoryLabel && categoryLabel}
        {!!categoryLabel && !!title && ': '}
        {!!title && title}
      </span>
    </Link>
  )
}
